import type {
  BlueprintId,
  ModuleBlueprint,
  ModuleId,
  ModuleInstance,
} from "./types";

/**
 * Registry — blueprint ve instance'ları bir araya getirir, tüm lookup'ları
 * tek yerden sunar. Uygulama genelinde `moduleRegistry` singleton'ı kullanılır;
 * factory testlerde farklı set vermek için dışa açık.
 */

export interface ModuleRegistryInput {
  blueprints: ReadonlyArray<ModuleBlueprint<unknown>>;
  instances: ReadonlyArray<ModuleInstance>;
}

export interface ModuleRegistry {
  instances: ReadonlyArray<ModuleInstance>;
  getBlueprint: (id: BlueprintId) => ModuleBlueprint<unknown>;
  getModuleById: (id: ModuleId) => ModuleInstance | undefined;
  /** Pathname'i basePath prefix'iyle eşleştirir (`/fashion/shop` → fashion). */
  getModuleByPath: (pathname: string) => ModuleInstance | undefined;
  /** Hostname'in ilk etiketini instance `subdomain`'leriyle eşleştirir. */
  getModuleByHost: (hostname: string) => ModuleInstance | undefined;
}

const stripTrailingSlash = (p: string) =>
  p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;

export const createModuleRegistry = ({
  blueprints,
  instances,
}: ModuleRegistryInput): ModuleRegistry => {
  const blueprintMap = new Map<BlueprintId, ModuleBlueprint<unknown>>();
  blueprints.forEach((bp) => blueprintMap.set(bp.id, bp));

  const getBlueprint = (id: BlueprintId) => {
    const bp = blueprintMap.get(id);
    if (!bp) {
      throw new Error(`[modules] Blueprint bulunamadı: "${id}"`);
    }
    return bp;
  };

  // Instance params'larını blueprint şemasıyla doğrula — hata build/dev'de
  // erken patlasın, runtime'da sessizce yanlış vertical gitmesin.
  const ids = new Set<ModuleId>();
  const basePaths = new Set<string>();
  instances.forEach((inst) => {
    if (ids.has(inst.id)) {
      throw new Error(`[modules] Aynı id iki kez tanımlı: "${inst.id}"`);
    }
    ids.add(inst.id);

    const base = stripTrailingSlash(inst.basePath);
    if (basePaths.has(base)) {
      throw new Error(`[modules] Aynı basePath iki kez tanımlı: "${base}"`);
    }
    basePaths.add(base);

    if (!inst.blueprint) {
      if (inst.status !== "soon") {
        throw new Error(
          `[modules] "${inst.id}" blueprint'siz; yalnızca status "soon" olabilir.`,
        );
      }
      return;
    }

    const parsed = getBlueprint(inst.blueprint).paramsSchema.safeParse(
      inst.params,
    );
    if (!parsed.success) {
      throw new Error(
        `[modules] "${inst.id}" params şemaya uymuyor: ${parsed.error.message}`,
      );
    }
  });

  // En uzun basePath önce — `/fashion-x` gibi çakışmalarda doğru eşleşme.
  const byPathLength = [...instances].sort(
    (a, b) => b.basePath.length - a.basePath.length,
  );

  const getModuleByPath = (pathname: string) => {
    const path = stripTrailingSlash(pathname);
    return byPathLength.find((inst) => {
      const base = stripTrailingSlash(inst.basePath);
      return path === base || path.startsWith(`${base}/`);
    });
  };

  const getModuleByHost = (hostname: string) => {
    const label = hostname.split(".")[0]?.toLowerCase();
    if (!label) return undefined;
    return instances.find(
      (inst) => inst.subdomain && inst.subdomain.toLowerCase() === label,
    );
  };

  return {
    instances,
    getBlueprint,
    getModuleById: (id) => instances.find((inst) => inst.id === id),
    getModuleByPath,
    getModuleByHost,
  };
};
